import eventsApi from 'api/requests/events';
import Button from 'components/_UI/Button';
import LinkPage from 'components/_UI/LinkPage';
import Loader from 'components/_UI/Loader';
import DynamicProfileBackground from 'components/commons/DynamicProfileBackground';
import { useBackButtonCollapseDash } from 'context/dash/BackButtonCollapseDash';
import { useShowAllEvents } from 'context/dash/ShowAllEvents';
import { useEvent } from 'context/events/Event';
import date from 'helpers/date';
import types from 'helpers/system/types';
import TicketsForSale from 'modules/dashboard/events/detail/tickets/TicketsForSale';
import CronogramModal from 'modules/events/detail/CronogramModal';
import React, { useState, useEffect } from 'react'
import { Copy, MapPin, Calendar, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import styled, { keyframes } from 'styled-components'
import { FullyCentered } from 'styled/alignment/Center';
import { Column } from 'styled/alignment/Column'
import colors from 'theme/colors';
import eventTypes from 'theme/eventTypes';
import ProductsForSale from '../products/ProductsForSale';
import breakpoints from 'theme/breakpoints';
import InfoTooltip from 'components/_UI/InfoTooltip';
import ToastContainer from 'components/_UI/ToastsContainer';
import useToast from 'hooks/useToast';

const truncateHTML = (html, limit) => {
  const div = document.createElement('div');
  div.innerHTML = html;
  const textContent = div.textContent || div.innerText || '';
  if (textContent.length <= limit) return html;
  div.innerHTML = textContent.slice(0, limit) + '...';
  return div.innerHTML;
};

export default function EventDetail({
  item,
  withBackButton = false,
  hasTickets,
  setHasTickets,
  setHasProducts,
  hasProducts,
  isDraft = false
}) {
  const [fullCronogramModal, setFullCronogramModal] = useState(false)
  const { showAllEvents, setShowAllEvents } = useShowAllEvents()
  const [showFullText, setShowFullText] = useState(false);
  const [visibleBackButton, setVisibleBackButton] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [offlineStats, setOfflineStats] = useState(null);
  const [offlineProductStats, setOfflineProductStats] = useState(null);
  const { backButtonCollapseDash, setBackButtonCollapseDash } = useBackButtonCollapseDash()
  const { toasts, toast } = useToast();
  const { setEvent } = useEvent();

  const eventUrl = `${import.meta.env.VITE_CLIENT_URL}/evento/${data?.slug}`;

  useEffect(() => {
    getEvent(item)
  }, [])

  const getEvent = async (slug) => {
    setLoading(true)
    try {
      const { data } = await eventsApi.getBySlug(slug)
      setData(data?.data)
      setEvent(data?.data);
      setHasTickets(data?.data?.products.some(el => el.type === 'ticket'))
      setHasProducts(data?.data?.products.some(el => el.type === 'product'))
    } catch (error) {
    } finally {
      setLoading(false)
    }

    try {
      const { data: ticketsRes } = await eventsApi.getTickets(slug);
      const sold = ticketsRes?.tickets_sold_offline;
      const revenue = ticketsRes?.tickets_revenue_offline;
      if (sold > 0 || revenue > 0) {
        setOfflineStats({ sold, revenue });
      }

      const soldProducts = ticketsRes?.products_sold_offline;
      const revenueProducts = ticketsRes?.products_revenue_offline;
      if (soldProducts > 0 || revenueProducts > 0) {
        setOfflineProductStats({ sold: soldProducts, revenue: revenueProducts });
      }
    } catch {}
  }

  const handleShowMore = () => setShowFullText(!showFullText);

  async function coppyEventUrl() {
    if (navigator?.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(eventUrl)
        toast.success('Link copiado com sucesso!', { position: 'top-center' })
      } catch (error) {
        copyToClipboardFallback(eventUrl)
      }
    }
  }

  function copyToClipboardFallback(value) {
    const input = document.createElement('input');
    input.value = value;
    document.body.appendChild(input);
    input.select();
    document.execCommand('copy');
    document.body.removeChild(input);
    toast.success('Link copiado com sucesso!', { position: 'top-center' })
  }

  return (
    <ContainerDetail>
      <ToastContainer toasts={toasts} />

      {!loading ? (
        <>
          {/* ── HERO CARD ── */}
          <HeroCard>
            <HeroImageWrap>
              <DynamicProfileBackground
                img={data?.images[0]}
                size={'260px'}
                event
              />
              <HeroOverlay />
            </HeroImageWrap>

            <HeroBody>
              {/* Badge row */}
              <BadgeRow>
                <StatusBadge active={data?.is_past}>
                  {data?.is_past ? 'Encerrado' : 'Ativo'}
                </StatusBadge>

                <TypeBadge type={data?.classification?.name}>
                  {types.getBySlug(data?.classification?.name)?.label}
                </TypeBadge>

                {data?.adulthood && <AdultBadge>+18</AdultBadge>}

                {data?.tags?.map(el => (
                  <TagBadge key={el.name}>{el.name}</TagBadge>
                ))}
              </BadgeRow>

              {/* Event name */}
              <EventName>{data?.name}</EventName>

              {/* Public link */}
              {!isDraft && (
                <LinkRow>
                  <ExternalLink size={14} color={colors.secondaryPurple} />
                  <LinkPage isWrapper to={eventUrl}>
                    <LinkText>{eventUrl}</LinkText>
                  </LinkPage>
                  <InfoTooltip
                    bgColor={colors.night}
                    text='Copiar link do evento'
                    type='none'
                  >
                    <CopyButton onClick={coppyEventUrl}>
                      <Copy size={12} color={colors.neutralLight} />
                    </CopyButton>
                  </InfoTooltip>
                </LinkRow>
              )}
            </HeroBody>
          </HeroCard>

          {/* ── INFO CARD ── */}
          <SectionCard>
            <SectionLabel>Informações</SectionLabel>

            <InfoItem>
              <IconWrap><MapPin size={16} color={colors.secondaryPurple} /></IconWrap>
              {!data?.address ? (
                <InfoText muted>Local a definir</InfoText>
              ) : (
                <LinkPage isWrapper to={data?.link_map}>
                  <InfoText link>
                    {data?.address?.street?.name && `${data?.address?.street?.name},`}
                    {data?.address?.street?.district && ` Bairro ${data?.address?.street?.district}`}
                    {data?.address?.number && `, nº ${data?.address?.number}`}
                    {data?.address?.complement && ` - ${data?.address?.complement}`}
                  </InfoText>
                </LinkPage>
              )}
            </InfoItem>

            <InfoDivider />

            <InfoItem alignTop={data?.dates?.length > 1}>
              <IconWrap><Calendar size={16} color={colors.secondaryPurple} /></IconWrap>
              <DateContainer>
                {data?.dates?.length > 1 ? (
                  <>
                    <DateLine>
                      <DayLabel>Dia 1</DayLabel>
                      <InfoText>
                        {date?.getDay(data?.dates[0]?.date)} de {date?.getMounthName(data?.dates[0]?.date)}
                        &nbsp;· {date?.getTimeWithoutSeconds(data?.dates[0]?.start)}
                        {data?.dates[0]?.end ? ` até ${date.getTimeWithoutSeconds(data?.dates[0]?.end)}` : ''}
                      </InfoText>
                    </DateLine>

                    <DateLine>
                      <DayLabel>Dia 2</DayLabel>
                      <InfoText>
                        {date?.getDay(data?.dates[1]?.date)} de {date?.getMounthName(data?.dates[1]?.date)}
                        &nbsp;· {date?.getTimeWithoutSeconds(data?.dates[1]?.start)}
                        {data?.dates[1]?.end ? ` até ${date.getTimeWithoutSeconds(data?.dates[1]?.end)}` : ''}
                      </InfoText>
                    </DateLine>

                    {data?.dates?.length >= 3 && (
                      <CronogramRow>
                        <Button
                          category='outline'
                          size='medium'
                          marginBottom='0'
                          onClick={() => setFullCronogramModal(true)}
                        >
                          Ver cronograma completo
                        </Button>
                        <CronogramModal
                          data={data?.dates}
                          visible={fullCronogramModal}
                          setVisible={setFullCronogramModal}
                        />
                      </CronogramRow>
                    )}
                  </>
                ) : (
                  <InfoText>
                    {data?.dates?.[0] && date?.getDay(data?.dates[0]?.date)}
                    {data?.dates?.[0] && ` de ${date?.getMounthName(data?.dates[0]?.date)}`}
                    {data?.dates?.[0] && ` · ${date?.getTimeWithoutSeconds(data?.dates[0]?.start)}`}
                    {data?.dates?.[0]?.end ? ` até ${date.getTimeWithoutSeconds(data?.dates[0]?.end)}` : ''}
                  </InfoText>
                )}
              </DateContainer>
            </InfoItem>
          </SectionCard>

          {/* ── DESCRIPTION CARD ── */}
          {(data?.description_with_html || data?.description) && (
            <SectionCard>
              <SectionLabel>Descrição</SectionLabel>

              {data?.description_with_html ? (
                <>
                  <DescriptionContent
                    dangerouslySetInnerHTML={{
                      __html: showFullText
                        ? data.description_with_html
                        : truncateHTML(data.description_with_html, 300),
                    }}
                  />
                  {data.description_with_html.length > 300 && (
                    <ShowMoreButton onClick={handleShowMore}>
                      {showFullText ? (
                        <><ChevronUp size={14} /> Ver menos</>
                      ) : (
                        <><ChevronDown size={14} /> Ver descrição completa</>
                      )}
                    </ShowMoreButton>
                  )}
                </>
              ) : (
                <>
                  <DescriptionText>
                    {data?.description?.length > 300 && !showFullText
                      ? `${data.description.slice(0, 285)}...`
                      : data?.description}
                  </DescriptionText>
                  {data?.description?.length > 300 && (
                    <ShowMoreButton onClick={() => setShowFullText(!showFullText)}>
                      {showFullText ? (
                        <><ChevronUp size={14} /> Ver menos</>
                      ) : (
                        <><ChevronDown size={14} /> Ver mais</>
                      )}
                    </ShowMoreButton>
                  )}
                </>
              )}
            </SectionCard>
          )}

          {/* ── TICKETS ── */}
          {!!hasTickets && (
            <SectionCard noPad>
              <TicketsForSale data={data?.products} offlineStats={offlineStats} />
            </SectionCard>
          )}

          {/* ── PRODUCTS ── */}
          {!!hasProducts && (
            <SectionCard noPad>
              <ProductsForSale data={data?.products} offlineStats={offlineProductStats} />
            </SectionCard>
          )}
        </>
      ) : (
        <LoaderWrap>
          <Loader />
        </LoaderWrap>
      )}
    </ContainerDetail>
  )
}

/* ─────────────────────────────────────────
   Animations
───────────────────────────────────────── */
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ─────────────────────────────────────────
   Layout
───────────────────────────────────────── */
const ContainerDetail = styled(Column)`
  min-height: 100vh;
  padding: 0 0 32px;
  gap: 12px;
  animation: ${fadeUp} 0.35s ease both;
`;

const LoaderWrap = styled(FullyCentered)`
  height: 100vh;
`;

/* ─────────────────────────────────────────
   Hero Card
───────────────────────────────────────── */
const HeroCard = styled.div`
  width: 100%;s
  border-radius: 14px;
  background: ${colors.neutralLight};
  border: 1px solid rgba(0,0,0,0.07);
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
`;

const HeroImageWrap = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: 14px 14px 0 0;

  & > div {
    width: 100% !important;
    height: 220px !important;
    border-radius: 0 !important;

    @media screen and (min-width: ${breakpoints.lg}) {
      height: 300px !important;
    }
  }

  & > div > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 0;
  }
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom,
    rgba(0,0,0,0) 40%,
    rgba(0,0,0,0.45) 100%
  );
  pointer-events: none;
`;

const HeroBody = styled.div`
  padding: 16px 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

/* ─────────────────────────────────────────
   Badges
───────────────────────────────────────── */
const BadgeRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
`;

const BaseBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  padding: 0 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: capitalize;
  white-space: nowrap;
`;

const StatusBadge = styled(BaseBadge).withConfig({
  shouldForwardProp: (prop) => prop !== 'active',
})`
  background: ${({ active }) => active ? '#f0f0f0' : '#e8f5ee'};
  color: ${({ active }) => active ? colors.night : '#1a7f45'};
`;

const TypeBadge = styled(BaseBadge)`
  background: ${({ type }) =>
    type === eventTypes?.free?.slug ? eventTypes?.free?.color :
    type === eventTypes?.paid?.slug ? eventTypes?.paid?.color :
    colors.neutralDark};
  color: ${colors.nightDark};
`;

const AdultBadge = styled(BaseBadge)`
  background: ${colors.nightDark};
  color: ${colors.neutralLight};
  min-width: 34px;
`;

const TagBadge = styled(BaseBadge)`
  background: ${colors.goldLight}33;
  color: ${colors.goldDark};
  border: 1px solid ${colors.goldLight}66;
`;

/* ─────────────────────────────────────────
   Event Name + Link
───────────────────────────────────────── */
const EventName = styled.h1`
  margin: 0;
  font-size: clamp(18px, 4vw, 26px);
  font-weight: 800;
  color: ${colors.nightDark};
  line-height: 1.2;
  letter-spacing: -0.3px;
`;

const LinkRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  overflow: hidden;
`;

const LinkText = styled.span`
  font-size: 12px;
  color: ${colors.primaryDark};
  text-decoration: underline;
  text-underline-offset: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 260px;
  cursor: pointer;
`;

const CopyButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  background: ${colors.secondaryPurple};
  transition: background 0.2s ease, transform 0.15s ease;

  &:hover {
    background: ${colors.occasionalPurple};
    transform: scale(1.08);
  }
`;

/* ─────────────────────────────────────────
   Section Card (reusable container)
───────────────────────────────────────── */
const SectionCard = styled.div`
  background: ${colors.neutralLight};
  border-radius: 14px;
  border: 1px solid rgba(0,0,0,0.07);
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
  padding: ${({ noPad }) => noPad ? '16px 18px' : '18px'};
  animation: ${fadeUp} 0.35s ease both;
`;

const SectionLabel = styled.p`
  margin: 0 0 14px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: ${colors.nightLight};
`;

/* ─────────────────────────────────────────
   Info Rows (location / date)
───────────────────────────────────────── */
const InfoItem = styled.div`
  display: flex;
  align-items: ${({ alignTop }) => alignTop ? 'flex-start' : 'center'};
  gap: 10px;
  padding: 2px 0;
`;

const IconWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: ${colors.secondaryPurple}14;
`;

const InfoText = styled.span`
  font-size: 13px;
  line-height: 1.5;
  color: ${({ muted }) => muted ? colors.nightLight : colors.nightDark};
  text-decoration: ${({ link }) => link ? 'underline' : 'none'};
  text-underline-offset: 2px;
  cursor: ${({ link }) => link ? 'pointer' : 'default'};
`;

const InfoDivider = styled.div`
  height: 1px;
  background: rgba(0,0,0,0.06);
  margin: 12px 0;
`;

/* ─────────────────────────────────────────
   Dates
───────────────────────────────────────── */
const DateContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
`;

const DateLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

const DayLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: ${colors.secondaryPurple};
  background: ${colors.secondaryPurple}14;
  padding: 2px 7px;
  border-radius: 4px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const CronogramRow = styled.div`
  margin-top: 8px;
`;

/* ─────────────────────────────────────────
   Description
───────────────────────────────────────── */
const DescriptionContent = styled.div`
  font-size: 13px;
  line-height: 1.7;
  color: ${colors.night};
  text-align: left;

  p, span, div { color: ${colors.night}; }
  strong, b    { color: ${colors.nightDark}; }
`;

const DescriptionText = styled.p`
  font-size: 13px;
  line-height: 1.7;
  color: ${colors.night};
  margin: 0;
`;

const ShowMoreButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 12px;
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid ${colors.secondaryPurple}55;
  background: transparent;
  font-size: 12px;
  font-weight: 600;
  color: ${colors.secondaryPurple};
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    background: ${colors.secondaryPurple};
    color: ${colors.neutralLight};
    border-color: ${colors.secondaryPurple};
  }
`;
